<script setup>
import {computed} from 'vue'
import {Link} from '@inertiajs/vue3'
import IconInfo from '@js/components/Icons/IconInfo.vue'
import {useProductStore} from '@js/store/ProductStore.js'

// Пропсы
const props = defineProps({
    // Группированные характеристики теперь приходят извне
    characteristics_grouped: {
        type: Array,
        default: () => [],
    },
    // Необязательно: если хотите полностью отказаться от стора —
    // передайте slug категории сюда
    categorySlug: {
        type: String,
        default: null,
    },
})

// Стор остаётся только как fallback для slug (если проп не передан)
const productStore = useProductStore()

// Берём сгруппированные хар-ки из пропсов
const characteristics_grouped = computed(() => props.characteristics_grouped ?? [])

// Пометка тех. хар-к
const isTech = (row) => row?.is_tech_characteristic

// Разделяем на общие и технические
const general = computed(() => characteristics_grouped.value.filter(r => !isTech(r)))

// slug категории: из пропса или (fallback) из стора
const categorySlug = computed(() => props.categorySlug ?? productStore.product?.category?.slug)
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div class="relative">
            <div class="px-4 sm:px-0 mb-2">
                <h2 class="text-base/7 font-semibold text-gray-900">Характеристики товара</h2>
            </div>
            <div class="border-t border-gray-200">
                <dl v-if="general.length">
                    <div
                        v-for="row in general"
                        :key="row.key || row.type_id"
                        class="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 odd:bg-white even:bg-gray-50 md:hover:bg-gray-100 transition"
                    >
                        <dt class="text-sm font-medium text-gray-900">
                            {{ row.title || row.title_en || 'Характеристика' }}
                        </dt>
                        <dd class="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 md:text-right">
                            <template v-if="Array.isArray(row.values)">
                                <Link
                                    v-for="(v, i) in row.values"
                                    :key="i"
                                    :href="route('catalog', {
                    category_title: categorySlug,
                    characteristics: JSON.stringify([{ type_id: row.type_id, values: [String(v).trim()] }]),
                  })"
                                    class="md:hover:text-navHoverColor transition"
                                >
                                    {{ v }}<span v-if="i < row.values.length - 1"> / </span>
                                </Link>
                            </template>
                            <template v-else>
                                {{ row.values }}
                            </template>
                        </dd>
                    </div>
                </dl>
                <div v-else class="p-4 text-sm text-gray-500">Характеристики не указаны.</div>
            </div>
        </div>

        <div>
            <div class="flex flex-col gap-2 text-xs leading-4">
                <p class="flex items-center gap-2 text-sm font-medium">
                    <IconInfo class="w-5 h-5"/>
                    Масса и расчёт цены (ЖБИ)
                </p>
                <p>
                    При продаже по цене за штуку или за м³ итоговая стоимость может определяться по фактической
                    массе/объёму партии по результатам взвешивания и/или обмера на складе.
                </p>
                <p>
                    При цене за м² или погонный метр расчёт ведётся по нормативной массе/объёму изделия согласно
                    проектным данным и действующим стандартам; возможна корректировка с учётом фактических допусков.
                </p>
                <p>
                    Класс бетона, тип/класс арматуры, стандарт (ГОСТ/ТУ), геометрические размеры, дата изготовления,
                    показатели морозостойкости и водонепроницаемости, а также сертификаты указываются в паспорте изделия
                    и/или на бирке упаковки. Для уточнения параметров конкретной партии обратитесь к менеджеру.
                </p>
            </div>
        </div>
    </div>
</template>
